/**
 * The authenticated frame — one boot call, one navigation, one top bar.
 *
 * Visual Reference: `design/screens/dashboard.html`. Its host is a column —
 * the top bar spanning the FULL width, then a row of sidebar and content
 * stretching beneath it — and the content pane carries `32px 24px 48px` with a
 * `--mdx-space-28` stack. `_system-sheet.html` §04/§05 specify the two pieces
 * in isolation and draw both as free-standing cards; where the specimen and the
 * screen disagree the screen wins, for the reason DECISION-039 gives.
 *
 * THE 375px MENU BUTTON STAYS ON A SETTINGS SCREEN, and the mockup does not
 * draw it there. At 375px `settings-members.html` replaces the whole settings
 * column — the back link included — with a section `select`, which leaves no
 * drawn route out of Settings on a phone. That is the dead end the same
 * mockup's own note warns against ("a disabled button with no route out is a
 * dead end"), so the top bar's existing `lg:hidden` menu button is kept. It
 * adds nothing the screen forbids and removes a trap.
 *
 * RESPONSIVE IS A COMPONENT DECISION, not a breakpoint cascade (ARCH §7.5).
 * Two widths only. At 1440px the sidebar is a permanent column; at 375px it is
 * not rendered at all and the same `<Sidebar>` appears in a sheet behind the
 * top bar's menu button. One component, two placements — the alternative is a
 * second nav that drifts from the first.
 *
 * WHAT THE SHELL DOES NOT DO. It does not gate anything. Every control it hides
 * has a server-side check behind it (`lib/route-contract.ts`), because RULE-009
 * makes UI hiding presentation only: "Removing UI restrictions in the browser
 * must grant no additional access — neither role nor entitlement."
 *
 * Story 3.8.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { apiPost } from '../lib/api'
import { Button } from '../ui/Button'
import type { NavAccess } from './nav'
import { SettingsLayout } from './SettingsLayout'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { useMe } from './useMe'
import { ShellContext, type Shell } from './workspace'

/**
 * Which workspace and org the shell is pointed at.
 *
 * Kept in `localStorage` so a reload lands where the person left off, and
 * validated against `/v1/me` on every boot — a remembered id whose grant was
 * revoked must fall back rather than render a workspace they can no longer
 * enter. The value is a convenience, never an authority: the server resolves
 * `{wid}` from the path on every request regardless of what is stored here.
 */
const REMEMBERED = 'leadgible.workspace'

function remembered(): string | null {
  try {
    return localStorage.getItem(REMEMBERED)
  } catch {
    // Private windows and blocked site data throw on access, not on read.
    return null
  }
}

function remember(id: string): void {
  try {
    localStorage.setItem(REMEMBERED, id)
  } catch {
    /* A preference that cannot be saved is not an error worth showing. */
  }
}

export function AppShell({
  path,
  settings = false,
  bare = false,
  children,
}: {
  path: string
  /**
   * Story 6.2's funnel editor: no top bar and no sidebar at all.
   *
   * `design/screens/editor-*.html` draws the editor's OWN top bar in place of
   * the app's, with "← Funnels" as the way out — so this is one step further
   * than `settings`, which swaps the sidebar and keeps the top bar. What the
   * shell still does here is everything invisible: the `/v1/me` boot call, the
   * signed-out redirect, and the workspace resolution the page reads through
   * `useShell()`. A page routed outside the shell would have to repeat all
   * three, and would get the workspace wrong.
   */
  bare?: boolean
  /**
   * Epic 4's screens replace the MAIN sidebar with the settings one.
   *
   * `settings-members.html` draws no Dashboard/Leads/Funnels column at all —
   * "← Back to Leadgible" is what leaves the section. So this is a swap rather
   * than a nesting, and the top bar is common to both.
   */
  settings?: boolean
  children: ReactNode
}) {
  const state = useMe()
  const [workspaceId, setWorkspaceId] = useState<string | null>(remembered)
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    if (state.status === 'signed-out') window.location.assign('/login')
  }, [state.status])

  if (state.status === 'loading') return <Centered>Loading your workspace…</Centered>
  if (state.status === 'signed-out') return <Centered>Taking you to the login page…</Centered>
  if (state.status === 'error') {
    return (
      <Centered>
        <p className="m-0 max-w-mdx-measure-48 text-mdx-16">{state.message}</p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Centered>
    )
  }

  const { me } = state

  // A remembered id that is no longer granted falls back to the first one the
  // server says they hold, which is the only list that is authoritative.
  const workspace =
    me.workspaces.find((w) => w.id === workspaceId) ?? me.workspaces[0] ?? undefined
  const org = me.orgs.find((o) => o.id === workspace?.orgId) ?? me.orgs[0] ?? undefined

  // One object for the whole subtree — the resolution above, not a repeat of it.
  const shell: Shell = { me, workspace, org }

  // Who is looking, for the nav (Story 22.5) — a prop to both sidebars, because
  // the 375px sheet below renders outside the provider.
  const access: NavAccess = { orgRole: org?.role ?? null, workspaceRole: workspace?.role ?? null }

  const chooseWorkspace = (id: string) => {
    setWorkspaceId(id)
    remember(id)
  }

  const chooseOrg = (id: string) => {
    // Switching org means switching to a workspace inside it; there is no
    // screen in the product that is org-scoped without one.
    const first = me.workspaces.find((w) => w.orgId === id)
    if (first) chooseWorkspace(first.id)
  }

  const signOut = () => {
    void (async () => {
      try {
        await apiPost('/v1/auth/sign-out', {})
      } catch {
        // A failed sign-out must still leave. The cookie may already be gone,
        // and stranding somebody on a dashboard they asked to leave is worse
        // than a redirect that outruns the request.
      }
      window.location.assign('/login')
    })()
  }

  return (
    <div className="flex min-h-screen flex-col bg-mdx-ground">
      {bare ? null : (
      <TopBar
        me={me}
        workspace={workspace}
        org={org}
        onWorkspace={chooseWorkspace}
        onOrg={chooseOrg}
        onSignOut={signOut}
        onOpenNav={() => setNavOpen(true)}
      />
      )}

      <ShellContext.Provider value={shell}>
        {bare ? (
          children
        ) : settings ? (
          <SettingsLayout path={path}>{children}</SettingsLayout>
        ) : (
          <div className="flex min-h-0 flex-1 items-stretch">
            <div className="hidden lg:flex">
              <Sidebar path={path} access={access} />
            </div>
            <main className="flex min-w-0 flex-1 flex-col gap-mdx-28 px-mdx-24 pb-mdx-48 pt-mdx-32">
              {children}
            </main>
          </div>
        )}
      </ShellContext.Provider>

      {/* The 375px navigation: the same component, in a sheet. */}
      {navOpen && !bare ? (
        <div className="fixed inset-0 z-20 flex lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            className="absolute inset-0 cursor-default border-0 bg-mdx-backdrop"
          />
          <div className="relative flex">
            <Sidebar path={path} access={access} onNavigate={() => setNavOpen(false)} />
          </div>
        </div>
      ) : null}
    </div>
  )
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-mdx-16 bg-mdx-ground px-mdx-32 text-center text-mdx-slate">
      {children}
    </main>
  )
}
